/**
 * Definición pura del contexto de auth.
 *
 * Separado de AuthProvider para que React Refresh funcione mejor:
 * la regla de react-refresh exige que cada archivo exporte solo
 * componentes, así que `createContext` y los tipos viven aquí.
 */

import { createContext } from "react";

import type { User } from "@/types/api";

export type AuthStatus =
  | "loading"
  | "anonymous"
  | "authenticated";

export interface AuthContextValue {
  user: User | null;
  status: AuthStatus;
  login: (email: string, password: string) => Promise<void>;
  /**
   * Register email+password. Sprint 7 Bloque B1: `inviteCode` obligatorio
   * durante la beta privada. Sprint 14 S14.3 (R2 GDPR): `acceptTerms`
   * obligatorio (consentimiento explícito de Términos + Política).
   */
  register: (
    email: string,
    password: string,
    inviteCode: string,
    fullName: string | undefined,
    acceptTerms: boolean,
  ) => Promise<void>;
  /**
   * Google Sign-In: recibe el id_token JWT que devuelve @react-oauth/google.
   * `inviteCode` solo se usa en signup (email nuevo); en signin el backend
   * lo ignora. La página /register lo pasa; /login lo omite.
   * `acceptTerms` (S14.3): consentimiento en signup social.
   */
  googleSignIn: (
    idToken: string,
    inviteCode?: string,
    acceptTerms?: boolean,
  ) => Promise<void>;
  /**
   * Apple Sign-In: recibe el id_token JWT y, opcionalmente, el name del
   * usuario (Apple SOLO lo entrega en el primer login).
   * `acceptTerms` (S14.3): consentimiento en signup social.
   */
  appleSignIn: (
    idToken: string,
    name?: { firstName: string; lastName: string },
    acceptTerms?: boolean,
  ) => Promise<void>;
  logout: () => Promise<void>;
  /**
   * Sprint 14 S14.5 (R3 GDPR): borra la cuenta y todos los datos. IRREVERSIBLE.
   * - Nativo: `{ password }`. Solo-OAuth: `{ confirm: true }`.
   * Tras éxito, limpia el estado de auth (queda anónimo). Propaga el error
   * (ej. invalid_password) para que la UI lo muestre.
   */
  deleteAccount: (args: {
    password?: string;
    confirm?: boolean;
  }) => Promise<void>;
}

export const AuthContext = createContext<AuthContextValue | null>(null);
